import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useBusiness, useAuth, api } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PieChart, Pie, Cell, ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar } from "recharts";
import TaxSnapshotCard from "@/components/tax/TaxSnapshotCard";
import InsightCard from "@/components/InsightCard";
import UpgradePrompt from "@/components/UpgradePrompt";

const Dashboard = () => {
  const { selectedBusiness, businesses, createBusiness, fetchBusinesses } = useBusiness();
  const { subscription } = useAuth();
  const hasWealthAccess = subscription?.is_active && subscription?.tier === "wealth_builder";
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [healthData, setHealthData] = useState(null);
  const [cashflowData, setCashflowData] = useState([]);
  const [expenseByCategory, setExpenseByCategory] = useState([]);
  const [insights, setInsights] = useState([]);
  const [insightsSummary, setInsightsSummary] = useState(null);
  const [wealthSummary, setWealthSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [newBusinessForm, setNewBusinessForm] = useState({
    name: "",
    industry: "",
    profit_percent: 10,
    tax_percent: 15,
    owner_pay_percent: 50,
    operating_expense_percent: 25,
  });

  useEffect(() => {
    api.get("/industries").then(r => setIndustries(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedBusiness) {
      fetchDashboard();
      fetchHealthData();
      fetchCashflow();
      fetchExpenseCategories();
      if (hasWealthAccess) {
        fetchInsights();
        fetchWealthSummary();
      }
    } else {
      setLoading(false);
    }
  }, [selectedBusiness, hasWealthAccess]);

  const fetchDashboard = async () => {
    try {
      const response = await api.get(`/dashboard/${selectedBusiness.id}`);
      setDashboardData(response.data);
    } catch (error) {
      console.error("Failed to fetch dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHealthData = async () => {
    try {
      const response = await api.get(`/financial-health/${selectedBusiness.id}`);
      setHealthData(response.data);
    } catch (error) {
      console.error("Failed to fetch health data:", error);
    }
  };

  const fetchCashflow = async () => {
    try {
      const response = await api.get(`/cashflow/${selectedBusiness.id}/from-transactions`);
      const months = (response.data.months || []).map((m) => ({
        ...m,
        label: new Date(m.month + "-01").toLocaleDateString("en-US", { month: "short", year: "2-digit" }),
      }));
      setCashflowData(months);
    } catch (error) {
      console.error("Failed to fetch cashflow:", error);
    }
  };

  const fetchExpenseCategories = async () => {
    try {
      const response = await api.get(`/profit-loss/${selectedBusiness.id}/from-transactions?period=ytd`);
      const items = (response.data.expense_items || [])
        .filter(i => i.amount > 0)
        .sort((a, b) => b.amount - a.amount)
        .slice(0, 8);
      setExpenseByCategory(items);
    } catch (error) {
      console.error("Failed to fetch expense categories:", error);
    }
  };

  const fetchInsights = async () => {
    try {
      const [insightsRes, summaryRes] = await Promise.all([
        api.get(`/insights/${selectedBusiness.id}`),
        api.get(`/insights/${selectedBusiness.id}/summary`)
      ]);
      setInsights(insightsRes.data);
      setInsightsSummary(summaryRes.data);
    } catch (error) {
      console.error("Failed to fetch insights:", error);
    }
  };

  const fetchWealthSummary = async () => {
    try {
      const res = await api.get(`/wealth/${selectedBusiness.id}/summary`);
      setWealthSummary(res.data);
    } catch (error) {
      console.error("Failed to fetch wealth summary:", error);
    }
  };

  const DONUT_COLORS = ["#1e3a5f", "#2d5a8e", "#3d7abd", "#6b9fd4", "#99c0e4", "#c2daf0", "#e0ecf8", "#f0f5fb"];
  const CATEGORY_LABELS = {
    opex: "Operating", income: "Income", tax: "Tax", profit: "Profit",
    uncategorized: "Other", meals: "Meals", travel: "Travel", supplies: "Supplies",
    software: "Software", marketing: "Marketing", rent: "Rent", utilities: "Utilities"
  };

  const handleCreateBusiness = async (e) => {
    e.preventDefault();
    const total = newBusinessForm.profit_percent + newBusinessForm.tax_percent + 
                  newBusinessForm.owner_pay_percent + newBusinessForm.operating_expense_percent;
    
    if (Math.abs(total - 100) > 0.01) {
      toast.error(`Percentages must sum to 100%. Current total: ${total}%`);
      return;
    }

    try {
      const biz = await createBusiness(newBusinessForm);
      // Apply industry template if selected
      if (newBusinessForm.industry && biz?.id) {
        try {
          await api.post(`/industries/apply/${biz.id}`, { industry_id: newBusinessForm.industry });
          toast.success("Business created with industry categories & deductions!");
        } catch {
          toast.success("Business created! (Industry setup can be done in Settings)");
        }
      } else {
        toast.success("Business created successfully!");
      }
      setShowCreateDialog(false);
      setNewBusinessForm({ name: "", industry: "", profit_percent: 10, tax_percent: 15, owner_pay_percent: 50, operating_expense_percent: 25 });
      fetchBusinesses();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create business");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getHealthColor = (score) => {
    if (score >= 80) return "text-[#1e3a5f]";
    if (score >= 60) return "text-blue-600";
    if (score >= 40) return "text-amber-600";
    return "text-red-600";
  };

  const getHealthBg = (score) => {
    if (score >= 80) return "bg-[#1e3a5f]";
    if (score >= 60) return "bg-blue-500";
    if (score >= 40) return "bg-amber-500";
    return "bg-red-500";
  };

  const COLORS = ["#1e3a5f", "#2d5a8e", "#3d7abd", "#6b9fd4"];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-muted-foreground">Loading dashboard...</div>
      </div>
    );
  }

  if (!selectedBusiness) {
    return (
      <div className="max-w-2xl mx-auto px-4">
        <Card className="border-dashed border-2 border-stone-300">
          <CardContent className="py-12 sm:py-16 text-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 rounded-full bg-[#1e3a5f]/10 flex items-center justify-center">
              <svg className="w-8 h-8 sm:w-10 sm:h-10 text-[#1e3a5f]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-stone-900 mb-2">Create Your First Business</h2>
            <p className="text-stone-600 mb-6 sm:mb-8 max-w-md mx-auto text-sm sm:text-base">
              Get started by adding your business with Opturna allocations.
            </p>
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="bg-[#1e3a5f] hover:bg-[#2d5a8e] w-full sm:w-auto" data-testid="create-first-business-btn">
                  Create Business
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md mx-4">
                <DialogHeader>
                  <DialogTitle>Create New Business</DialogTitle>
                  <DialogDescription>Set up Opturna allocation percentages.</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateBusiness} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Business Name</Label>
                    <Input id="name" value={newBusinessForm.name} onChange={(e) => setNewBusinessForm({ ...newBusinessForm, name: e.target.value })} placeholder="My Business" required data-testid="business-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="industry">Industry</Label>
                    <select
                      id="industry"
                      value={newBusinessForm.industry}
                      onChange={(e) => setNewBusinessForm({ ...newBusinessForm, industry: e.target.value })}
                      className="w-full px-3 py-2 rounded-md border border-[#e4e8ee] text-sm focus:outline-none focus:ring-1 focus:ring-[#1e3a5f]/20"
                      data-testid="business-industry-select"
                    >
                      <option value="">Select your industry...</option>
                      {industries.map(ind => (
                        <option key={ind.id} value={ind.id}>{ind.name}</option>
                      ))}
                    </select>
                    <p className="text-[10px] text-[#8b95a5]">Auto-populates expense categories, deductions & categorization rules for your industry</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Profit %</Label>
                      <Input type="number" min="0" max="100" value={newBusinessForm.profit_percent} onChange={(e) => setNewBusinessForm({ ...newBusinessForm, profit_percent: parseFloat(e.target.value) || 0 })} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Tax %</Label>
                      <Input type="number" min="0" max="100" value={newBusinessForm.tax_percent} onChange={(e) => setNewBusinessForm({ ...newBusinessForm, tax_percent: parseFloat(e.target.value) || 0 })} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Owner Pay %</Label>
                      <Input type="number" min="0" max="100" value={newBusinessForm.owner_pay_percent} onChange={(e) => setNewBusinessForm({ ...newBusinessForm, owner_pay_percent: parseFloat(e.target.value) || 0 })} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">OpEx %</Label>
                      <Input type="number" min="0" max="100" value={newBusinessForm.operating_expense_percent} onChange={(e) => setNewBusinessForm({ ...newBusinessForm, operating_expense_percent: parseFloat(e.target.value) || 0 })} />
                    </div>
                  </div>
                  <p className="text-xs text-stone-500">Total: {newBusinessForm.profit_percent + newBusinessForm.tax_percent + newBusinessForm.owner_pay_percent + newBusinessForm.operating_expense_percent}% (must be 100%)</p>
                  <Button type="submit" className="w-full bg-[#1e3a5f] hover:bg-[#2d5a8e]" data-testid="submit-create-business">Create Business</Button>
                </form>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      </div>
    );
  }

  const allocationData = dashboardData?.totals ? [
    { name: "Profit", value: dashboardData.totals.profit, color: "#059669" },
    { name: "Tax", value: dashboardData.totals.tax, color: "#f59e0b" },
    { name: "Owner Pay", value: dashboardData.totals.owner_pay, color: "#8b5cf6" },
    { name: "OpEx", value: dashboardData.totals.operating_expense, color: "#ef4444" },
  ].filter(d => d.value > 0) : [];

  return (
    <div className="space-y-4 sm:space-y-6 pb-20" data-testid="dashboard-page">
      {/* Mobile Health Score Card */}
      {healthData && (
        <Card className="overflow-hidden">
          <div className={`h-2 ${getHealthBg(healthData.health_score)}`} />
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs text-stone-500 uppercase tracking-wide">Financial Health</p>
                <div className="flex items-baseline gap-2">
                  <span className={`text-4xl sm:text-5xl font-bold ${getHealthColor(healthData.health_score)}`}>
                    {healthData.health_score}
                  </span>
                  <span className="text-stone-400">/100</span>
                </div>
                <Badge variant="outline" className={`mt-1 ${getHealthColor(healthData.health_score)}`}>
                  {healthData.status.replace("_", " ").toUpperCase()}
                </Badge>
              </div>
              <div className="w-20 h-20 sm:w-24 sm:h-24">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={[
                        { value: healthData.health_score },
                        { value: 100 - healthData.health_score }
                      ]}
                      innerRadius="60%"
                      outerRadius="100%"
                      startAngle={90}
                      endAngle={-270}
                      dataKey="value"
                    >
                      <Cell fill={healthData.health_score >= 60 ? "#10b981" : "#f59e0b"} />
                      <Cell fill="#e5e7eb" />
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Quick Stats Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="p-3 rounded-lg bg-[#1e3a5f]/5">
                <p className="text-xs text-[#1e3a5f] mb-1">Profit Margin</p>
                <p className="text-lg sm:text-xl font-bold text-[#1e3a5f]">{healthData.metrics.profit_margin}%</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-50">
                <p className="text-xs text-blue-600 mb-1">Runway</p>
                <p className="text-lg sm:text-xl font-bold text-blue-700">{healthData.metrics.runway_months} mo</p>
              </div>
              <div className="p-3 rounded-lg bg-amber-50">
                <p className="text-xs text-amber-600 mb-1">Tax Saved</p>
                <p className="text-lg sm:text-xl font-bold text-amber-700">{formatCurrency(healthData.metrics.tax_saved)}</p>
              </div>
              <div className="p-3 rounded-lg bg-violet-50">
                <p className="text-xs text-violet-600 mb-1">Reserve</p>
                <p className="text-lg sm:text-xl font-bold text-violet-700">{formatCurrency(healthData.metrics.reserve_balance)}</p>
              </div>
            </div>

            {/* Next Allocation Reminder */}
            {healthData.next_allocation && (
              <div className="mt-4 p-3 rounded-lg bg-stone-50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#1e3a5f]/10 flex items-center justify-center">
                    <span className="text-lg">📅</span>
                  </div>
                  <div>
                    <p className="font-medium text-stone-900 text-sm">Next Allocation</p>
                    <p className="text-xs text-stone-500">{healthData.next_allocation.date}</p>
                  </div>
                </div>
                <Badge className="bg-[#1e3a5f]/10 text-[#1e3a5f]">
                  {healthData.next_allocation.days_until} days
                </Badge>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Upgrade Prompt for non-Wealth Builder users */}
      {!hasWealthAccess && (
        <UpgradePrompt feature="Insights & Wealth Engine" requiredTier="Wealth Builder" compact />
      )}

      {/* Insights & Action Steps */}
      {hasWealthAccess && insights.length > 0 && (
        <Card className="overflow-hidden" data-testid="insights-section">
          <div className="h-1.5 bg-gradient-to-r from-[#1e3a5f] via-[#2d5a8e] to-emerald-500" />
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  Action Steps
                  {insightsSummary && (
                    <Badge className="bg-[#1e3a5f] text-white text-[10px]">{insightsSummary.total_insights}</Badge>
                  )}
                </CardTitle>
                <CardDescription className="text-xs">
                  {insightsSummary?.total_financial_impact > 0
                    ? `${formatCurrency(insightsSummary.total_financial_impact)} in potential impact detected`
                    : "Personalized recommendations based on your data"
                  }
                </CardDescription>
              </div>
              {insightsSummary && insightsSummary.completed > 0 && (
                <Badge variant="outline" className="text-emerald-700 bg-emerald-50 border-emerald-200 text-xs">
                  {insightsSummary.completed} completed
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {insights.slice(0, 4).map((insight) => (
              <InsightCard
                key={insight.rule_id}
                insight={insight}
                businessId={selectedBusiness.id}
                onUpdate={fetchInsights}
              />
            ))}
            {insights.length > 4 && (
              <p className="text-xs text-center text-stone-400 pt-1">
                +{insights.length - 4} more insights available
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Wealth Summary Widget */}
      {hasWealthAccess && wealthSummary && (
        <Card className="overflow-hidden" data-testid="wealth-summary-widget">
          <div className="h-1.5 bg-gradient-to-r from-emerald-500 via-[#2d5a8e] to-[#1e3a5f]" />
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  Wealth Engine
                  {wealthSummary.suggestions_count > 0 && (
                    <Badge className="bg-emerald-600 text-white text-[10px]">{wealthSummary.suggestions_count} suggestions</Badge>
                  )}
                </CardTitle>
                <CardDescription className="text-xs">
                  Capital deployment & goal tracking
                </CardDescription>
              </div>
              <Button variant="ghost" size="sm" className="text-xs text-[#1e3a5f]" onClick={() => navigate("/wealth")} data-testid="wealth-view-all">
                View Details
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Deploy + Metrics Row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="p-3 rounded-lg bg-emerald-50 col-span-2 sm:col-span-1">
                <p className="text-[10px] text-emerald-600 uppercase tracking-wide">Available to Deploy</p>
                <p className="text-xl font-bold text-emerald-700" data-testid="wealth-deploy-amount">{formatCurrency(wealthSummary.available_to_deploy)}</p>
              </div>
              <div className="p-3 rounded-lg bg-stone-50">
                <p className="text-[10px] text-stone-500">Total Balance</p>
                <p className="text-lg font-bold text-stone-700">{formatCurrency(wealthSummary.total_balance)}</p>
              </div>
              <div className="p-3 rounded-lg bg-amber-50">
                <p className="text-[10px] text-amber-600">Obligations</p>
                <p className="text-lg font-bold text-amber-700">{formatCurrency(wealthSummary.obligations_total)}</p>
              </div>
              <div className={`p-3 rounded-lg ${wealthSummary.monthly_net >= 0 ? 'bg-[#1e3a5f]/5' : 'bg-red-50'}`}>
                <p className={`text-[10px] ${wealthSummary.monthly_net >= 0 ? 'text-[#1e3a5f]' : 'text-red-600'}`}>Monthly Net</p>
                <p className={`text-lg font-bold ${wealthSummary.monthly_net >= 0 ? 'text-[#1e3a5f]' : 'text-red-700'}`}>{formatCurrency(wealthSummary.monthly_net)}</p>
              </div>
            </div>

            {/* Goals Progress */}
            {wealthSummary.goals_count > 0 ? (
              <div className="p-3 rounded-lg bg-stone-50">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-stone-700">
                    Goal Progress ({wealthSummary.goals_completed}/{wealthSummary.goals_count} complete)
                  </p>
                  <span className="text-xs font-semibold text-[#1e3a5f]">{wealthSummary.goals_overall_progress}%</span>
                </div>
                <Progress value={Math.min(100, wealthSummary.goals_overall_progress)} className="h-2" />
                <div className="flex items-center justify-between mt-2 text-[10px] text-stone-400">
                  <span>{formatCurrency(wealthSummary.goals_total_current)} saved</span>
                  <span>{formatCurrency(wealthSummary.goals_total_target)} target</span>
                </div>
              </div>
            ) : (
              <div className="p-3 rounded-lg bg-stone-50 flex items-center justify-between">
                <p className="text-xs text-stone-500">No goals yet — set your first financial goal</p>
                <Button variant="outline" size="sm" className="text-xs" onClick={() => navigate("/wealth")} data-testid="wealth-create-goal-cta">
                  + Create Goal
                </Button>
              </div>
            )}

            {/* Top Suggestion */}
            {wealthSummary.top_suggestion && (
              <div className="flex items-center gap-3 p-3 rounded-lg border border-dashed" style={{ borderColor: `${wealthSummary.top_suggestion.color}40` }}>
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${wealthSummary.top_suggestion.color}15` }}>
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: wealthSummary.top_suggestion.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-stone-800">{wealthSummary.top_suggestion.label}</p>
                  <p className="text-[10px] text-stone-500 truncate">{wealthSummary.top_suggestion.description}</p>
                </div>
                <span className="text-sm font-bold flex-shrink-0" style={{ color: wealthSummary.top_suggestion.color }}>{formatCurrency(wealthSummary.top_suggestion.amount)}</span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* YTD Financial Summary from Transactions */}
      {dashboardData?.transaction_totals && dashboardData.transaction_totals.transaction_count > 0 && (
        <Card data-testid="ytd-financial-summary">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">YTD Financial Summary</CardTitle>
              <Badge variant="outline">{dashboardData.transaction_totals.transaction_count} transactions</Badge>
            </div>
            <CardDescription className="text-xs">Based on recorded transactions this year</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 rounded-lg bg-[#1e3a5f]/5">
                <p className="text-xs text-[#1e3a5f] mb-1">Income</p>
                <p className="text-lg font-bold text-[#1e3a5f]" data-testid="ytd-income">
                  {formatCurrency(dashboardData.transaction_totals.ytd_income)}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-red-50">
                <p className="text-xs text-red-600 mb-1">Expenses</p>
                <p className="text-lg font-bold text-red-700" data-testid="ytd-expenses">
                  {formatCurrency(dashboardData.transaction_totals.ytd_expenses)}
                </p>
              </div>
              <div className={`p-3 rounded-lg ${dashboardData.transaction_totals.ytd_net >= 0 ? 'bg-[#1e3a5f]/5' : 'bg-red-50'}`}>
                <p className={`text-xs ${dashboardData.transaction_totals.ytd_net >= 0 ? 'text-[#1e3a5f]' : 'text-red-600'} mb-1`}>Net Profit</p>
                <p className={`text-lg font-bold ${dashboardData.transaction_totals.ytd_net >= 0 ? 'text-[#1e3a5f]' : 'text-red-700'}`} data-testid="ytd-net">
                  {formatCurrency(dashboardData.transaction_totals.ytd_net)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions - Mobile Optimized */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Button
          variant="outline"
          className="h-auto py-4 flex flex-col items-center gap-2"
          onClick={() => navigate("/opturna")}
          data-testid="quick-action-pf"
        >
          <span className="text-2xl">💰</span>
          <span className="text-xs font-medium">Run Allocation</span>
        </Button>
        <Button
          variant="outline"
          className="h-auto py-4 flex flex-col items-center gap-2"
          onClick={() => navigate("/allocation")}
          data-testid="quick-action-calculator"
        >
          <span className="text-2xl">🧮</span>
          <span className="text-xs font-medium">Calculator</span>
        </Button>
        <Button
          variant="outline"
          className="h-auto py-4 flex flex-col items-center gap-2"
          onClick={() => navigate("/tax-plans")}
          data-testid="quick-action-tax"
        >
          <span className="text-2xl">📋</span>
          <span className="text-xs font-medium">Tax Plan</span>
        </Button>
        <Button
          variant="outline"
          className="h-auto py-4 flex flex-col items-center gap-2"
          onClick={() => navigate("/deductions")}
          data-testid="quick-action-deductions"
        >
          <span className="text-2xl">📖</span>
          <span className="text-xs font-medium">Deductions</span>
        </Button>
      </div>

      {/* Monthly Cash Flow Trend Chart */}
      {cashflowData.length > 0 && (
        <Card data-testid="cashflow-trend-chart">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Monthly Cash Flow</CardTitle>
              <Button variant="ghost" size="sm" className="text-xs text-[#1e3a5f]" onClick={() => navigate("/transactions")} data-testid="view-transactions-link">
                View Transactions
              </Button>
            </div>
            <CardDescription className="text-xs">Income vs. expenses from recorded transactions</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-56 sm:h-64 -ml-2">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={cashflowData} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#78716c" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#78716c" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(v >= 1000 ? 0 : 1)}k`} width={48} />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 12 }}
                    formatter={(value) => [`$${Number(value).toLocaleString()}`, undefined]}
                  />
                  <Bar dataKey="revenue" name="Income" fill="#1e3a5f" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="expenses" name="Expenses" fill="#e57373" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            {/* Legend */}
            <div className="flex items-center justify-center gap-6 mt-2">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm bg-[#1e3a5f]" />
                <span className="text-xs text-[#5a6577]">Income</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm bg-[#e57373]" />
                <span className="text-xs text-[#5a6577]">Expenses</span>
              </div>
            </div>
            {/* Net Profit Trend */}
            <div className="mt-4 h-24 -ml-2">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={cashflowData}>
                  <defs>
                    <linearGradient id="netGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1e3a5f" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#1e3a5f" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="label" hide />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 12 }}
                    formatter={(value) => [`$${Number(value).toLocaleString()}`, "Net Profit"]}
                  />
                  <Area type="monotone" dataKey="net" stroke="#1e3a5f" fill="url(#netGradient)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <p className="text-center text-[10px] text-stone-400 mt-1">Net Profit Trend</p>
          </CardContent>
        </Card>
      )}

      {/* Expense Category Breakdown Donut */}
      {expenseByCategory.length > 0 && (
        <Card data-testid="expense-category-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-[#1e3a5f]">Where Your Money Goes</CardTitle>
            <CardDescription className="text-xs">YTD expense breakdown by category</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="w-44 h-44 flex-shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={expenseByCategory}
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={68}
                      dataKey="amount"
                      nameKey="category"
                      stroke="none"
                    >
                      {expenseByCategory.map((_, index) => (
                        <Cell key={index} fill={DONUT_COLORS[index % DONUT_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`${formatCurrency(value)}`, undefined]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 space-y-1.5">
                {expenseByCategory.map((item, index) => (
                  <div key={item.category} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: DONUT_COLORS[index % DONUT_COLORS.length] }} />
                      <span className="text-[#5a6577] capitalize">{CATEGORY_LABELS[item.category] || item.category}</span>
                    </div>
                    <span className="font-medium text-[#1e3a5f]">{formatCurrency(item.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recommendations */}
      {healthData?.recommendations?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <span>💡</span> Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {healthData.recommendations.slice(0, 3).map((rec, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-[#1e3a5f]/5">
                  <div className="w-5 h-5 rounded-full bg-[#1e3a5f]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-[#1e3a5f]">{idx + 1}</span>
                  </div>
                  <p className="text-sm text-[#1e3a5f]">{rec}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Allocation Summary */}
      {dashboardData && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Allocation Summary</CardTitle>
              <Badge variant="outline">{dashboardData.totals?.allocation_count || 0} records</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              {allocationData.length > 0 ? (
                <>
                  <div className="w-32 h-32 sm:w-40 sm:h-40">
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie
                          data={allocationData}
                          innerRadius="50%"
                          outerRadius="100%"
                          dataKey="value"
                        >
                          {allocationData.map((entry, idx) => (
                            <Cell key={idx} fill={entry.color} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex-1 grid grid-cols-2 gap-2 w-full">
                    {allocationData.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 rounded bg-stone-50">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-stone-500 truncate">{item.name}</p>
                          <p className="font-medium text-sm">{formatCurrency(item.value)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-8 w-full">
                  <p className="text-stone-500 text-sm mb-4">No allocations recorded yet</p>
                  <Button variant="outline" size="sm" onClick={() => navigate("/allocation")}>
                    Make First Allocation
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Business Settings */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Opturna Settings</CardTitle>
          <CardDescription className="text-xs">{selectedBusiness.name}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-2 text-center">
            <div className="p-2 rounded bg-[#1e3a5f]/5">
              <p className="text-xs text-[#1e3a5f]">Profit</p>
              <p className="font-bold text-[#1e3a5f]">{selectedBusiness.profit_percent}%</p>
            </div>
            <div className="p-2 rounded bg-amber-50">
              <p className="text-xs text-amber-600">Tax</p>
              <p className="font-bold text-amber-700">{selectedBusiness.tax_percent}%</p>
            </div>
            <div className="p-2 rounded bg-violet-50">
              <p className="text-xs text-violet-600">Owner</p>
              <p className="font-bold text-violet-700">{selectedBusiness.owner_pay_percent}%</p>
            </div>
            <div className="p-2 rounded bg-red-50">
              <p className="text-xs text-red-600">OpEx</p>
              <p className="font-bold text-red-700">{selectedBusiness.operating_expense_percent}%</p>
            </div>
          </div>
          <Button variant="outline" size="sm" className="w-full mt-4" onClick={() => navigate("/settings")}>
            Manage Settings
          </Button>
        </CardContent>
      </Card>

      {/* Tax Snapshot */}
      <div className="lg:col-span-2">
        <TaxSnapshotCard />
      </div>
    </div>
  );
};

export default Dashboard;
